import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    ButtonBase,
    FormLabel,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import { FormComponentBuilder } from 'components/common/form/layout';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormElementProps } from 'components/common/form/index';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { Popup } from 'components/common/popup/Popup';
import IconHandler from 'components/common/icon/IconHandler';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { ALL_TYPES } from '../useResourceTypeFilter';
import { isDeleted, isUnpublished } from '../resourceOptionParts';
import ResourceStateChip from '../ResourceStateChip';

interface DataResourcePickerProps extends FormControlProps {
    data: Array<FormComponentValue>;
    elementProps: FormElementProps;
    // Whether the knowledge-graph pool is empty; absent, an empty option list stands in for it.
    poolEmpty?: boolean;
}

function ResourceIcon({ option }: { option?: FormComponentValue }) {
    const icon = option?.props?.icon;
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0 }}>
            {icon ? <IconHandler icon={icon} /> : <IconMi icon="globe" fontSize="16" />}
        </Box>
    );
}

function StateChip({ option }: { option: FormComponentValue }) {
    if (isDeleted(option)) return <ResourceStateChip state="deleted" />;
    if (isUnpublished(option)) return <ResourceStateChip state="unpublished" />;
    return null;
}

export default function DataResourcePicker({ controlProps }: FormElementControlPropsType<DataResourcePickerProps>) {
    const { t } = useBundleTranslation('app/editor/element/extreport');
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const value = controlProps.value ? String(controlProps.value) : '';
    const name = controlProps.name;

    // Some sources prepend a synthetic '--' row; the picker is clearable either way.
    const options = useMemo(() => controlProps.data.filter((opt) => '' !== String(opt.value)), [controlProps.data]);
    const selected = options.find((opt) => String(opt.value) === value);

    const [typeFilter, setTypeFilter] = useState(ALL_TYPES);
    const typeFilterOptions: Array<FormComponentValue> = useMemo(() => {
        const types = new Map<string, string>();
        options.forEach((opt) => {
            const key = String(opt.props?.logo_key ?? '');
            if (key) types.set(key, String(opt.props?.type_label ?? key));
        });
        return [
            { value: ALL_TYPES, label: t('concierge_resource_picker_filter_all') },
            ...Array.from(types.entries()).map(([value, label]) => ({ value, label })),
        ];
    }, [options, t]);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return options.filter((opt) => {
            if (typeFilter !== ALL_TYPES && opt.props?.logo_key !== typeFilter) return false;
            return (
                !needle ||
                String(opt.label).toLowerCase().includes(needle) ||
                String(opt.props?.type_label ?? '')
                    .toLowerCase()
                    .includes(needle)
            );
        });
    }, [options, search, typeFilter]);

    const poolEmpty = controlProps.poolEmpty ?? 0 === options.length;

    const close = () => {
        setOpen(false);
        setSearch('');
        setTypeFilter(ALL_TYPES);
    };
    const pick = (next: string) => {
        controlProps.onChange(next);
        close();
    };

    return (
        <Stack>
            <FormLabel id={`${name}_label`}>{controlProps.label ?? t('concierge_answer_data_resource')}</FormLabel>
            <Stack direction="row">
                <ButtonBase
                    data-test={`${name}_display`}
                    aria-labelledby={`${name}_label ${name}_value`}
                    disabled={controlProps.readOnly}
                    onClick={() => setOpen(true)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        justifyContent: 'flex-start',
                        gap: 1,
                        px: 1.5,
                        minHeight: 28,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRight: 0,
                        borderRadius: '3px 0 0 3px',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        color: 'primary.main',
                        textAlign: 'left',
                    }}
                >
                    <ResourceIcon option={selected} />
                    <Box
                        component="span"
                        id={`${name}_value`}
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {selected
                            ? `${selected.label} · ${selected.props?.short_label ?? selected.props?.type_label ?? ''}`
                            : t('concierge_resource_picker_title')}
                    </Box>
                    {selected && <StateChip option={selected} />}
                </ButtonBase>
                <Button
                    variant="contained"
                    data-test={`${name}_edit`}
                    disabled={controlProps.readOnly}
                    onClick={() => setOpen(true)}
                    startIcon={<IconMi icon="gear" fontSize="16" />}
                    sx={{ borderRadius: '0 3px 3px 0' }}
                >
                    {t('concierge_resource_picker_edit')}
                </Button>
            </Stack>
            {poolEmpty && (
                <Typography
                    data-test="concierge_insight_resources_empty_hint"
                    sx={{ mt: 1, fontSize: '12px', lineHeight: '18px', color: 'text.secondary' }}
                >
                    {t('concierge_insight_resources_empty_hint')}
                </Typography>
            )}
            {open && (
                <Popup
                    open
                    settings={{
                        title: t('concierge_resource_picker_title'),
                        needTranslation: false,
                        noOk: true,
                        noCancel: true,
                    }}
                    maxWidth="popupLg"
                    onHide={close}
                    additionalButtons={
                        value
                            ? [
                                  <Button
                                      key="clear"
                                      variant="outlined"
                                      color="neutral"
                                      data-test={`${name}_clear`}
                                      onClick={() => pick('')}
                                      sx={{ mr: 1 }}
                                  >
                                      {t('concierge_resource_picker_clear')}
                                  </Button>,
                              ]
                            : undefined
                    }
                >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            sx={{ flex: 1, minWidth: 0 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('concierge_resource_picker_search')}
                            data-test={`${name}_search`}
                            inputProps={{ 'aria-label': t('concierge_resource_picker_search') }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconMi icon="search" fontSize="14" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ flex: '0 0 auto', width: 280, maxWidth: '55%' }}
                        >
                            <StaticAddon>{t('concierge_resource_picker_filter_by_type')}</StaticAddon>
                            <Box sx={{ flexGrow: 1 }}>
                                <ReactSelect
                                    data={typeFilterOptions}
                                    value={typeFilter}
                                    update={(newVal) => setTypeFilter((newVal as string) || ALL_TYPES)}
                                    name={`${name}_type_filter`}
                                    isMulti={false}
                                    isClearable={false}
                                    dataTest={`${name}_type_filter`}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('concierge_resource_picker_col_name')}</TableCell>
                                <TableCell>{t('concierge_grid_col_type')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((opt) => (
                                <TableRow
                                    key={String(opt.value)}
                                    hover
                                    selected={String(opt.value) === value}
                                    data-test={`${name}_row_${opt.value}`}
                                    tabIndex={0}
                                    onClick={() => pick(String(opt.value))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            pick(String(opt.value));
                                        }
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ResourceIcon option={opt} />
                                            <span>{opt.label}</span>
                                            <StateChip option={opt} />
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{opt.props?.type_label ?? ''}</TableCell>
                                </TableRow>
                            ))}
                            {0 === rows.length && (
                                <TableRow data-test={`${name}_no_match`}>
                                    <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>
                                        {t('concierge_resource_picker_no_match')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Popup>
            )}
        </Stack>
    );
}

export class DataResourcePickerBuilder extends FormComponentBuilder {
    prepareProps(): DataResourcePickerProps {
        return {
            ...this.controlProps,
            data: this.componentValues,
            elementProps: this.elementProps,
            poolEmpty: this.elementProps.componentProps?.poolEmpty,
        };
    }
}
